import {
  Box,
  Card,
  Divider,
  Grid,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { Loader, LoadingButton } from "../../../shared";
import { useThemeConfig } from "../hooks";
import { TenantInfoText } from "../components";

export const ThemeConfig = () => {
  const colorFields = [
    ["primary_color", "Primary Color"],
    ["secondary_color", "Secondary Color"],
    ["primary_text_color", "Primary Text Color"],
    ["secondary_text_color", "Secondary Text Color"],
    ["text_color", "Base Text Color"],
    ["accent_color", "Accent Color"],
  ];

  const displaySections = [
    { key: "top_navbar", label: "Top Navbar", platforms: ["app", "web"] },
    { key: "bottom_navbar", label: "Bottom Navbar", platforms: ["app", "web"] },
    { key: "footer", label: "Footer Web", platforms: ["web"] },
  ];

  const { state, onColorChange, onDisplayChange, onSubmit } = useThemeConfig();
  const { formData } = state;

  return (
    <Card component="form" onSubmit={onSubmit} sx={{ p: { xs: 2, sm: 1.5 } }}>
      <TenantInfoText infoKey="themeConfig" alert sx={{ mb: "24px" }}/>
      {state.isLoading ? (
        <Stack height={400} justifyContent="center" alignItems="center">
          <Loader />
        </Stack>
      ) : (
        <Stack gap={2}>
          <Grid container spacing={1}>
            {colorFields.map(([name, label]) => (
              <Grid item xs={12} sm={6} key={name}>
                <Stack
                  component="label"
                  direction="row"
                  alignItems="center"
                  gap={1}
                  sx={{
                    p: "10px",
                    border: "1px solid #E6E6E6",
                    borderRadius: 1,
                    backgroundColor: "#F9FAFC",
                    cursor: "pointer",
                  }}
                >
                  <Box
                    component="input"
                    type="color"
                    name={name}
                    value={formData[name]}
                    onChange={onColorChange}
                    aria-label={label}
                    sx={{
                      width: 32,
                      height: 32,
                      p: 0,
                      flexShrink: 0,
                      bgcolor: formData[name],
                      cursor: "pointer",
                      border: "1px solid #bdbdbd",
                      borderRadius: "6px",
                      overflow: "hidden",
                      appearance: "none",
                      "&::-webkit-color-swatch-wrapper": {
                        padding: 0,
                      },
                      "&::-webkit-color-swatch": {
                        border: "none",
                        borderRadius: "6px",
                      },
                    }}
                  />
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      display="block"
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="caption"
                      lineHeight={1}
                      color="text.secondary"
                    >
                      {formData[name]}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>

         <TenantInfoText infoKey="darkTheme" alert/>

          <Stack gap={1.5}>
            {displaySections.map(({ key, label, platforms }) => (
              <Box key={key}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ mb: 1.5 }}
                >
                  {label}
                </Typography>
                <Grid container spacing={1}>
                  {platforms.map((platform) => {
                    const settings = formData[key][platform];
                    return (
                      <Grid
                        item
                        xs={12}
                        sm={platforms.length > 1 ? 6 : 12}
                        key={platform}
                      >
                        <Card
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderRadius: "12px",
                            bgcolor: "#F9FAFC",
                            boxShadow: "none",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            textTransform="capitalize"
                          >
                            {platform}
                          </Typography>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mt: "8px" }}
                          >
                            <Typography variant="body1">Theme-Dark</Typography>
                            <Switch
                              checked={settings.dark_mode}
                              onChange={(event) =>
                                onDisplayChange(
                                  key,
                                  platform,
                                  "dark_mode",
                                  event.target.checked,
                                )
                              }
                            />
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mt: "8px" }}
                          >
                            <Typography variant="body1">
                              Background color
                            </Typography>
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap={1}
                              sx={{
                                height: 36,
                                px: "8px",
                                border: "1px solid #c7d0dc",
                                borderRadius: "12px",
                                bgcolor: "#fff",
                              }}
                            >
                              <Box
                                component="input"
                                type="color"
                                value={settings.background_color}
                                onChange={(event) =>
                                  onDisplayChange(
                                    key,
                                    platform,
                                    "background_color",
                                    event.target.value,
                                  )
                                }
                                aria-label={`${platform} background color`}
                                sx={{
                                  width: 20,
                                  height: 20,
                                  p: 0,
                                  border: "1px solid #bdbdbd",
                                  borderRadius: "5px",
                                  overflow: "hidden",
                                  appearance: "none",
                                  cursor: "pointer",
                                  bgcolor: settings.background_color,
                                  "&::-webkit-color-swatch-wrapper": { p: 0 },
                                  "&::-webkit-color-swatch": {
                                    border: "none",
                                    borderRadius: "4px",
                                  },
                                }}
                              />
                              <Typography variant="body1">
                                {settings.background_color.toUpperCase()}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            ))}
          </Stack>

          <Divider />
          <LoadingButton
            type="submit"
            variant="contained"
            loading={state.isSubmitting}
            disabled={!state.hasChanges}
            sx={{ ml: "auto", minWidth: 48 }}
          >
            Save
          </LoadingButton>
        </Stack>
      )}
    </Card>
  );
};
